import { subscribeToAnySignal } from 'serviceWorker/events/swSignal';
import { swSignals } from 'serviceWorker/events/swSignals';

const STOPPING_SIGNALS = [swSignals.loggedOut, swSignals.projectDataReplaced];

export type LogoutAbortMode = 'exclusive' | 'concurrent';

export type LogoutAbortRegistry = {
  createController: () => AbortController;
  releaseController: (controller: AbortController) => void;
  abortCurrent: () => void;
};

/**
 * Abort registry that always aborts on logout / project flush.
 *
 * - exclusive: creating a new controller aborts the previous in-flight request
 * - concurrent: in-flight requests keep running until they finish or logout
 */
export function createLogoutAbortRegistry(
  mode: LogoutAbortMode = 'exclusive'
): LogoutAbortRegistry {
  let currentController: AbortController | null = null;
  const concurrentControllers = new Set<AbortController>();

  const abortCurrent = (): void => {
    currentController?.abort();
    currentController = null;
    concurrentControllers.forEach((controller) => {
      controller.abort();
    });
    concurrentControllers.clear();
  };

  subscribeToAnySignal(STOPPING_SIGNALS, abortCurrent);

  return {
    createController: () => {
      if (mode === 'exclusive') {
        currentController?.abort();
        currentController = new AbortController();
        return currentController;
      }

      const controller = new AbortController();
      concurrentControllers.add(controller);
      return controller;
    },
    releaseController: (controller: AbortController): void => {
      if (mode === 'exclusive') {
        if (currentController === controller) {
          currentController = null;
        }
        return;
      }

      concurrentControllers.delete(controller);
    },
    abortCurrent,
  };
}

/**
 * Simple wrapper to create a new abort controller for each request,
 * while keeping the logout listener at init level.
 *
 * Useful when you need a fresh controller per request (e.g., for debounced handlers).
 * Pass `concurrent` when overlapping signed requests must not abort each other.
 */
export function createAbortOnLogout(
  mode: LogoutAbortMode = 'exclusive'
): LogoutAbortRegistry {
  return createLogoutAbortRegistry(mode);
}
